import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  effect,
  inject,
  DestroyRef,
  HostListener,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DataService } from "../data.service";
import { HttpClient } from "@angular/common/http";
import {
  BehaviorSubject,
  combineLatest,
  defer,
  distinctUntilChanged,
  from,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from "rxjs";
import { SellsDialogComponent } from "./sells-dialog/sells-dialog.component";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { db, qService } from "src/app/db.service";
import { slideAnimation, toggleWrap } from "../shared/fade.animation";
import { liveQuery } from "dexie";
import {
  ColumnState,
  GetRowIdFunc,
  GridApi,
  GridReadyEvent,
} from "ag-grid-community";
import * as _moment from "moment";
const moment = _moment;
import * as _latinize from "latinize";
import { PusherService } from "../pusher.service";
import { ProductsService } from "../products/products.service";
import { StockService } from "../stock/stock.service";
const latinize = _latinize;

@Component({
  selector: "app-sells",
  templateUrl: "./sells.component.html",
  styleUrls: ["./sells.component.css"],
  animations: [slideAnimation, toggleWrap],
})
export class SellsComponent implements OnInit {
  _dataService = inject(DataService);
  _stockService = inject(StockService);
  destroyRef = inject(DestroyRef);

  @HostListener("window:keyup", ["$event"])
  keyEvent(event: KeyboardEvent) {
    if (event.key == "Escape") {
      this._dataService.showContextMenu = false;
    }
  }

  dataLoading: boolean = true;
  @ViewChild("cardsContainer") cardsContainer: ElementRef;
  @ViewChild("searchInput") searchInput: ElementRef;
  dateNow = moment(Date.now()).format("YYYY-MM-DD");
  canDelete: boolean = false;
  componentDestroyed$ = new Subject<void>();
  showSummary: boolean = localStorage.hasOwnProperty(
    this._dataService.dbTable + "showSellsSummary"
  )
    ? this._dataService.getLS("showSellsSummary")
    : true;

  isWrapped: boolean = false;

  showTotal: boolean = false;

  navButtons = [
    {
      name: "Yeni Satış",
      icon: "add",
      function: () => this.openSellsDialog(),
    },
    {
      name: "Qayıtma",
      icon: "remove",
      function: () => this.openSellsDialog(null, "Return"),
    },
  ];

  dbUpdated: boolean;
  _dbUpdating$ = new BehaviorSubject(true);

  sellsSumDataUnfiltered: {}[] = this._dataService.getLS("sellsSumData") || [];
  sellsSumData = this._dataService.getLS("sellsSumData") || [];
  sellsSumTotal: number = this._dataService.getLS("sellsSumTotal") || 0;
  sellsReturnTotal: number = this._dataService.getLS("sellsReturnTotal") || 0;

  contextMenuData = { x: 0, y: 0, data: null };
  hoverData;
  unitTotals = [];
  showUnitTotals: boolean = false;

  protected gridApi!: GridApi;
  agGridData$: Observable<any[]>;
  columnDefs = [
    {
      headerName: "id",
      field: "id",
      hide: true,
    },
    {
      headerName: "Tərəf Müqabil",
      field: "customerName",
      sortable: true,
      flex: 3,
      minWidth: 150,
      filterValueGetter: (params) =>
        this._dataService.latinizeText(params, "customerName"),
      cellRenderer: (params) => {
        let res = params.data.refersTo
          ? `${params.value}<span>R</span>`
          : params.value;
        if (params.data.syncStatus == "pending") {
          res += `<span class="material-symbols-outlined" style="font-size: 18px;">
          cloud_sync
          </span>`;
        }
        return res;
      },
      cellClass: (params) => {
        return params.data.refersTo
          ? "editedTransaction loadingCell"
          : "loadingCell";
      },
    },
    {
      headerName: "Toplam, AZN",
      field: "sum",
      sortable: true,
      flex: 2,
      minWidth: 120,
      cellClass: (params) => {
        return params.value < 0 ? "minusValueRedColor" : "";
      },
      cellRenderer: (params) => {
        return typeof params.value === "number"
          ? params.value
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
          : "";
      },
      cellStyle: { "word-spacing": "-2px !important", "white-space": "nowrap" },
    },
    {
      headerName: "Tarix",
      field: "date",
      flex: 2,
      minWidth: 120,
      sortable: true,
      comparator: (dateA, dateB, nodeA, nodeB, isInverted) => {
        if (dateA === dateB) {
          // If dates are equal, sort by id (descending)
          const idA = nodeA.data.id;
          const idB = nodeB.data.id;
          return idA > idB ? -1 : idA < idB ? 1 : 0;
        } else {
          // Sort by date (descending)
          return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
        }
      },
      cellRenderer: (params) => {
        return params.value.split("-").reverse().join("-");
      },
    },
    {
      headerName: "Məlumat",
      field: "details",
      flex: 2,
      minWidth: 100,
      cellClass: "justify-content-start",
    },
    {
      headerName: "",
      field: "delete",
      flex: 1,
      maxWidth: 50,
      cellRenderer: () => {
        return `<span class='d-flex align-items-center h-100'><i class='material-icons'>more_horiz</i></span>`;
      },
      onCellClicked: (params) => {
        this.contextMenu(params);
      },
    },
  ];
  public getRowId: GetRowIdFunc = (params) => params.data?.id?.toString();

  constructor(
    private http: HttpClient,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private _pusher: PusherService,
    protected _productService: ProductsService
  ) {
    effect(async () => {
      this._dataService.dbChanged();
      this._dataService.products();
      this._dataService.materials();
      this._dataService.customers();
      this.agGridData$ = this.getSellsTableData();
      this._dataService.dbChanged();
    });
  }

  ngOnInit() {
    this.agGridData$ = this.getSellsTableData();
  }

  getSellsTableData() {
    const localData = this._dataService.getLS("sellsTable") || [];
    const localObservable = defer(() => {
      return of(localData);
    });
    const liveQueryObservable =
      this._dataService.startDate.value && this._dataService.endDate.value
        ? combineLatest([
            this._dataService.dateChanged,
            this._dbUpdating$.pipe(distinctUntilChanged()),
          ]).pipe(
            tap(() => {
              this.dataLoading = true;
            }),
            switchMap(() =>
              from(
                liveQuery(() =>
                  this.getTransactions(
                    this._dataService.startDate.value.format("YYYY-MM-DD"),
                    this._dataService.endDate.value.format("YYYY-MM-DD")
                  )
                )
              ).pipe(
                map((things) =>
                  things.map((thing) => {
                    const customer = this._dataService
                      .customers()
                      .find((c) => c.id === thing.customerId);
                    return {
                      ...thing,
                      sum: thing.type === "Sells" ? thing.sum : -thing.sum,
                      customerName: customer
                        ? customer.hasOwnProperty("name")
                          ? customer.name
                          : ""
                        : thing.customerId === 0
                        ? "Pərakəndə"
                        : "",
                    };
                  })
                ),
                tap((data) => {
                  this.sellsSumTotal = data.reduce(
                    (acc, value) =>
                      value.type == "Sells" ? acc + value.sum : acc,
                    0
                  );

                  this.sellsReturnTotal = data.reduce(
                    (acc, value) =>
                      value.type != "Sells" ? acc + value.sum : acc,
                    0
                  );
                  this._dataService.setLS("sellsSumTotal", this.sellsSumTotal);
                  this._dataService.setLS(
                    "sellsReturnTotal",
                    this.sellsReturnTotal
                  );
                  this._dataService.setLS("sellsTable", data.slice(-100));

                  setTimeout(() => {
                    this.dataLoading = false;
                    if (
                      this.searchInput &&
                      this.searchInput.nativeElement.value
                    ) {
                      this.applyFilter(this.searchInput.nativeElement.value);
                    }
                  }, 500);
                })
              )
            ),
            takeUntilDestroyed(this.destroyRef)
          )
        : [];

    return merge(localObservable, liveQueryObservable);
  }

  async getTransactions(startDate: Date, endDate: Date) {
    const sellsTransactions = await db.transactions
      .where("date")
      .between(startDate, endDate, true, true)
      .toArray()
      .then((transaction) =>
        transaction.filter((t) => t.type == "Sells" || t.type == "Return")
      );

    this.getSellTotalsByProduct(startDate, endDate);
    return sellsTransactions;
  }

  async getSellTotalsByProduct(startDate: any, endDate: any) {
    type ProductTotal = {
      productId: number | null;
      materialId: number | null;
      productName: string;
      sumBetweenDates: number;
      sumBeforeStartDate: number;
      productPrice: number | null; // Standard/list price of the item
      productUnit?: string;
      totalQuantity: number;
      totalQuantityBeforeStartDate: number;
      type: string;
      formulaCost: number; // Represents the unit cost of the item
      revenue: number | null; // Calculated revenue
    };

    const productsArray: any = this._dataService.products();
    const materialsArray: any = this._dataService.materials();
    const formulaGroupsArray: any = this._dataService.formulaGroups();

    const productsMap: any = new Map(productsArray.map((p) => [p.id, p]));
    const materialsMap: any = new Map(materialsArray.map((m) => [m.id, m]));
    const formulaGroupsMap: any = new Map(
      formulaGroupsArray.map((fg) => [fg.id, fg])
    );

    function calculateProductUnitCost(pId: any): number {
      const productDetails: any = productsMap.get(pId);
      if (!productDetails) return 0;

      let baseFormulaCostPart: number = 0;
      let uzlemeCostPart: number = 0;

      if (productDetails.formulaGroupId) {
        const formula: any = formulaGroupsMap.get(
          productDetails.formulaGroupId
        );
        const formulaCostValue: number = formula?.cost || 0;

        if (typeof productDetails.weight === "number") {
          baseFormulaCostPart =
            productDetails.width && productDetails.width !== 0
              ? (formulaCostValue * productDetails.weight) /
                productDetails.width
              : formulaCostValue * productDetails.weight;
          if (isNaN(baseFormulaCostPart) || baseFormulaCostPart === undefined) {
            baseFormulaCostPart = 0;
          }
        }
      }
      if (typeof productDetails.uzleme === "number") {
        uzlemeCostPart = productDetails.uzleme;
      }
      const totalUnitCost: number = baseFormulaCostPart + uzlemeCostPart;
      return isNaN(totalUnitCost) ? 0 : totalUnitCost;
    }

    const sells: any = await db.sells
      .where("date")
      .between(startDate, endDate, true, true)
      .toArray();

    const sellsBeforeStartDate: any = await db.sells
      .where("date")
      .below(startDate)
      .toArray();

    const productTotals: { [key: string]: ProductTotal } = {};

    sells.forEach((sell: any) => {
      const { productId, materialId, quantity, price } = sell;
      const numQuantity: number = Number(quantity) || 0;
      const sellPrice: number = Number(price) || 0;
      const currentSellValue: number = numQuantity * sellPrice;

      let originalId: any;
      let itemType: string;
      let accumulatorKey: string;

      if (productId) {
        originalId = productId;
        itemType = "product";
        accumulatorKey = `product_${productId}`;
      } else if (materialId) {
        originalId = materialId;
        itemType = "material";
        accumulatorKey = `material_${materialId}`;
      } else {
        return;
      }

      if (!productTotals[accumulatorKey]) {
        let name: string = "",
          itemStandardPrice: number | null = null,
          unit: string = "",
          itemUnitCost: number = 0;

        if (itemType === "product") {
          const pDetails: any = productsMap.get(originalId);
          name = pDetails?.name || "Unknown Product";
          itemStandardPrice = pDetails?.sellPrice;
          unit = pDetails?.unit || "";
          itemUnitCost = calculateProductUnitCost(originalId);
        } else {
          // material
          const mDetails: any = materialsMap.get(originalId);
          name = `${mDetails?.name || "Unknown Material"}`;
          itemStandardPrice = mDetails?.price; // Material's own price from master data
          unit = mDetails?.unitType || "";
          itemUnitCost = mDetails?.price || 0; // Material's own price is its unit cost
        }
        productTotals[accumulatorKey] = {
          productId: itemType === "product" ? Number(originalId) : null,
          materialId: itemType === "material" ? Number(originalId) : null,
          productName: name,
          sumBetweenDates: 0,
          sumBeforeStartDate: 0,
          productPrice: itemStandardPrice,
          productUnit: unit,
          totalQuantity: 0,
          totalQuantityBeforeStartDate: 0,
          type: itemType,
          formulaCost: itemUnitCost, // This field now stores the unit cost for the item
          revenue: 0, // Initialize revenue to 0 for accumulation
        };
      }

      productTotals[accumulatorKey].sumBetweenDates += currentSellValue;
      productTotals[accumulatorKey].totalQuantity += numQuantity;

      const costForThisTransactionLine: number =
        numQuantity * productTotals[accumulatorKey].formulaCost;
      productTotals[accumulatorKey].revenue +=
        currentSellValue - costForThisTransactionLine;
    });

    sellsBeforeStartDate.forEach((sell: any) => {
      const { productId, materialId, quantity, price } = sell;
      const numQuantity: number = Number(quantity) || 0;
      const sellPrice: number = Number(price) || 0;
      const currentSellValue: number = numQuantity * sellPrice;

      let originalId: any;
      let itemType: string;
      let accumulatorKey: string;

      if (productId) {
        originalId = productId;
        itemType = "product";
        accumulatorKey = `product_${productId}`;
      } else if (materialId) {
        originalId = materialId;
        itemType = "material";
        accumulatorKey = `material_${materialId}`;
      } else {
        return;
      }

      if (!productTotals[accumulatorKey]) {
        let name: string = "",
          itemStandardPrice: number | null = null,
          unit: string = "",
          itemUnitCost: number = 0;
        if (itemType === "product") {
          const pDetails: any = productsMap.get(originalId);
          name = pDetails?.name || "Unknown Product";
          itemStandardPrice = pDetails?.sellPrice;
          unit = pDetails?.unit || "";
          itemUnitCost = calculateProductUnitCost(originalId);
        } else {
          // material
          const mDetails: any = materialsMap.get(originalId);
          name = `${mDetails?.name || "Unknown Material"}`;
          itemStandardPrice = mDetails?.price;
          unit = mDetails?.unitType || "";
          itemUnitCost = mDetails?.price || 0;
        }
        productTotals[accumulatorKey] = {
          productId: itemType === "product" ? Number(originalId) : null,
          materialId: itemType === "material" ? Number(originalId) : null,
          productName: name,
          sumBetweenDates: 0,
          sumBeforeStartDate: 0,
          productPrice: itemStandardPrice,
          productUnit: unit,
          totalQuantity: 0,
          totalQuantityBeforeStartDate: 0,
          type: itemType,
          formulaCost: itemUnitCost,
          revenue: 0, // Revenue for "before" items might not be primary focus but init for consistency
        };
      }
      productTotals[accumulatorKey].sumBeforeStartDate += currentSellValue;
      productTotals[accumulatorKey].totalQuantityBeforeStartDate += numQuantity;
    });

    Object.values(productTotals).forEach((item) => {
      if (typeof item.revenue === "number") {
        item.revenue = parseFloat(item.revenue.toFixed(2));
      }
      // Ensure other sums are also formatted if they haven't been implicitly
      item.sumBetweenDates = parseFloat(item.sumBetweenDates.toFixed(2));
      item.sumBeforeStartDate = parseFloat(item.sumBeforeStartDate.toFixed(2));
    });

    const result: any = Object.values(productTotals);
    result.sort((a, b) => b.sumBetweenDates - a.sumBetweenDates);
    const filteredResult: any = result.filter(
      (item) => item.sumBetweenDates !== 0 || item.sumBeforeStartDate !== 0
    ); // Keep if has any sum
    this.sellsSumData = filteredResult;
    this._dataService.setLS("sellsSumData", filteredResult);

    this.unitTotals = this.sellsSumData
      .reduce((acc, item) => {
        const existing = acc.find((i) => i.unit === item.productUnit);
        if (existing) {
          existing.sum += item.totalQuantity;
        } else {
          acc.push({ unit: item.productUnit, sum: item.totalQuantity });
        }
        return acc;
      }, [])
      .map((i) => ({ unit: i.unit, sum: i.sum.toFixed(2) }));

    return filteredResult;
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.setGridOption("includeHiddenColumnsInQuickFilter", true);
    var defaultSortModel: ColumnState[] = [
      { colId: "date", sort: "asc", sortIndex: 0 },
    ];
    params.api.applyColumnState({ state: defaultSortModel });
  }

  applyFilter(val, input?) {
    if (!val && input) {
      input.value = "";
    }
    this.gridApi.setGridOption("quickFilterText", val);
    this.sellsSumTotal = 0;
    this.sellsReturnTotal = 0;
    this.gridApi.forEachNodeAfterFilterAndSort((node) => {
      this.sellsSumTotal += node.data.sum >= 0 ? node.data.sum : 0;
      this.sellsReturnTotal += node.data.sum < 0 ? node.data.sum : 0;
    });
    this.sellsSumData = this.sellsSumDataUnfiltered.filter(function (o: any) {
      return (
        latinize(o.productName.replace(/\s/g, "").toLowerCase()).indexOf(
          latinize(val.replace(/\s/g, "").toLowerCase())
        ) > -1
      );
    });
  }

  onWheel(event: WheelEvent): void {
    if (
      !this.cardsContainer?.nativeElement?.className?.includes("expandedGraphs")
    ) {
      if (event.deltaMode === WheelEvent.DOM_DELTA_PIXEL) {
        this.cardsContainer.nativeElement.scrollLeft += event.deltaY;
        // event.preventDefault();
      }
    }
  }

  productDetailsDialog(product) {
    if (product.productId) {
      this._productService.ProductDetailsDialog(product.productId);
    }
    if (product.materialId) {
      this._stockService.materialDialog(product.materialId);
    }
  }

  deleteEntry(row) {
    this._dataService.showContextMenu = false;
    let data = row.customerName;
    if (row.sum)
      data += ` - ` + row.sum.toFixed(3).replace(/\.(\d\d)\d?$/, ".$1") + " ₼";
    else data += ` - 0.00 ₼`;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "500px",
      data: data,
    });
    dialogRef.afterClosed().subscribe(async (res) => {
      if (res) {
        try {
          const registration = await navigator.serviceWorker.ready;
          await (registration as any).sync.register("test-sync");

          const payload = {
            type: "REMOVE_TRANSACTION",
            removeTransactionId: row.id,
            dbName: this._dataService.dbTable,
            userId: this._dataService.userId,
          };

          await qService.requests.add({
            payload,
            timestamp: Date.now(),
          });

          await (registration as any).sync.register("app-sync");
          await db.transactions.delete(row.id);
          await db.sells.where("tId").equals(row.id).delete();
          let gridRow = this.gridApi.getRowNode(row.id);
          if (gridRow) {
            this.gridApi.applyTransaction({
              remove: [gridRow],
            });
            this.gridApi.refreshCells();
          }
        } catch {
          this.dataLoading = true;
          this.http
            .put(
              this._dataService.dbBase +
                "api.php/records/transactions/" +
                row.id,
              { status: 1, userId: this._dataService.userId }
            )
            .subscribe(async () => {
              await db.transactions.delete(row.id);
              await db.sells.where("tId").equals(row.id).delete();
              let gridRow = this.gridApi.getRowNode(row.id);
              if (gridRow) {
                this.gridApi.applyTransaction({
                  remove: [gridRow],
                });
                this.gridApi.refreshCells();
              }
              this._pusher.postData("Sell Entry Deleted").subscribe();
              this.toastr.success("", "Silindi!", {
                toastClass: "ngx-toastr customToast",
                progressBar: true,
                timeOut: 2500,
                progressAnimation: "increasing",
              });
              setTimeout(() => {
                this.dataLoading = false;
              }, 700);
            });
        }
      }
    });
  }

  openSellsDialog(
    transactionId?,
    transactionType?: string,
    rowData?,
    event?,
    editMode?: boolean,
    copyMode?: boolean
  ) {
    if ((event && event.event.target.innerText != "more_horiz") || !event) {
      this._dataService.showContextMenu = false;
      let data = null;
      if (transactionId) {
        data = rowData;
        data.transactionId = transactionId;
        data.editMode = editMode;
        data.copyMode = copyMode;
      } else {
        data = { type: transactionType || "Sells" };
      }
      const dialogRef = this.dialog.open(SellsDialogComponent, {
        width: transactionId ? "1000px" : "950px",
        maxHeight: "700px",
        data: data,
        closeOnNavigation: true,
        disableClose: this._dataService.mobileDevice
          ? false
          : editMode || copyMode || !transactionId
          ? true
          : false,
      });
      dialogRef.afterClosed().subscribe((res) => {
        if (res) {
          const startDate = new Date(
            this._dataService.startDate.value.format("YYYY-MM-DD")
          );
          const endDate = new Date(
            this._dataService.endDate.value.format("YYYY-MM-DD")
          );
          const targetDate = new Date(res.date);
          if (targetDate < startDate || targetDate > endDate) {
            this.gridApi.applyTransaction({
              add: [
                {
                  ...res,
                  // bankGroup: res.bankGroupName,
                  // transactionId: res.tId,
                  // transaction: res.transactionId,
                  // justAdded: true,
                },
              ],
            });
            if (res.remove) {
              let row = this.gridApi.getRowNode(res.remove);
              this.gridApi.applyTransaction({
                remove: [row],
              });
              this.gridApi.refreshCells();
            }
            // setTimeout(() => {
            //   let row = this.gridApi.getRowNode(res.id);
            //   row.data.justAdded = false;
            //   this.gridApi.applyTransaction({
            //     update: [row],
            //   });
            // }, 3000);
          }

          // let data = { ...res, bankGroupId: "loading" };
        }
      });
    }
  }

  contextMenu(data) {
    let lastOpened = this.contextMenuData.data
      ? this.contextMenuData.data.id
      : null;
    data.event.preventDefault();
    const targetElement = data.event.target as HTMLElement;
    const rect = targetElement.getBoundingClientRect();

    this.contextMenuData = {
      x: null,
      y: rect.bottom + window.scrollY,
      data: data.data,
    };
    this._dataService.showContextMenu =
      lastOpened == this.contextMenuData.data.id
        ? !this._dataService.showContextMenu
        : true;
  }

  onCellMouseOver(params) {
    if (!this._dataService.showContextMenu)
      this.contextMenuData.data = params.data;
    this.hoverData = params.data;
  }

  onRightClick(event?) {
    event.preventDefault();
    if (this.contextMenuData.data != this.hoverData) {
      this.contextMenuData.data = this.hoverData;
    }
    const targetElement = event.target as HTMLElement;
    const rect = targetElement.getBoundingClientRect();

    this.contextMenuData.x = event.clientX;
    this.contextMenuData.y = rect.bottom + window.scrollY;

    this._dataService.showContextMenu = true;
  }
}
